// FamilyCapturePad — Jul 2026 (Randy, Phase A "Family Access Point").
// The capture pad for FAMILY MEMBERS (free accounts invited to a personal
// silo). Mirrors the Staging tab's Capture pad — dictate/type, then Smart
// create (calendar items, approval rules applied server-side) or Push to
// silo — with NONE of the creator-dashboard chrome around it.
import { useEffect, useRef, useState } from "react";
import { Lightning, Microphone, Stop, PaperPlaneRight, Sparkle, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import DocSaveDialog from "@/components/DocSaveDialog";
import SplitAndPushModal, { needsSplit } from "@/components/SplitAndPushModal";
import { EventEditorModal } from "@/components/CalendarSection";
import SmartCaptureModal from "@/components/SmartCaptureModal";
import SmartEditPreviewModal from "@/components/SmartEditPreviewModal";
import SmartEditUndoCenter from "@/components/SmartEditUndoCenter";
import InfoTip from "@/components/InfoTip";
import { useEntitlements } from "@/lib/entitlements";

export default function FamilyCapturePad({ siloId, siloName, siloSlug, inheritedSkus = [] }) {
  const { owns } = useEntitlements();
  const [text, setText] = useState("");
  // Feb 22, 2026 (Randy pt89c-iii) — persist the capture pad text so a
  // browser refresh / accidental logout / tab switch doesn't lose it.
  // Keyed by silo so different silos don't cross-contaminate.
  const LS_KEY = `mysilo:capture-pad-text:${siloId}`;
  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(LS_KEY);
      if (saved) setText(saved);
    } catch { /* localStorage unavailable */ }
  }, [siloId]);
  useEffect(() => {
    try {
      if (text) window.localStorage.setItem(LS_KEY, text);
      else window.localStorage.removeItem(LS_KEY);
    } catch { /* ignore */ }
  }, [LS_KEY, text]);
  // Feb 2026 (Randy) — Push-to-silo tier default is now silo-kind-aware
  // (Tier 1 for personal / organizer; Tier 2 for public / YT-Twin).
  // Fetched via the calendar settings endpoint since FamilyCapturePad
  // doesn't get the silo doc from a parent list.
  const [siloKind, setSiloKind] = useState("personal");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/calendar/${siloId}/settings`);
        if (!cancelled) setSiloKind(r.data?.silo_kind || "personal");
      } catch { /* stay on default */ }
    })();
    return () => { cancelled = true; };
  }, [siloId]);
  const defaultPushTier = (siloKind === "personal" || siloKind === "organizer") ? 1 : 2;
  const [commitState, setCommitState] = useState({ open: false, text: "", defaultTitle: "" });
  const [splitState, setSplitState] = useState({ open: false, content: "", tier: defaultPushTier, title: "" });

  // ---- voice dictation (same engine as the Staging pad: Whisper, owner-pays) ----
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [recElapsed, setRecElapsed] = useState(0);
  const [liveTranscript, setLiveTranscript] = useState("");
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const recognitionRef = useRef(null);

  const startLivePreview = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    try {
      const rec = new SR();
      rec.continuous = true;
      rec.interimResults = true;
      rec.lang = navigator.language || "en-US";
      let finalSoFar = "";
      rec.onresult = (ev) => {
        let interim = "";
        for (let i = ev.resultIndex; i < ev.results.length; i++) {
          const t = ev.results[i][0].transcript;
          if (ev.results[i].isFinal) finalSoFar += t + " ";
          else interim += t;
        }
        setLiveTranscript((finalSoFar + interim).trim());
      };
      rec.onerror = () => {};
      recognitionRef.current = rec;
      rec.start();
    } catch { /* preview is best-effort */ }
  };
  const stopLivePreview = () => {
    try { recognitionRef.current?.stop(); } catch { /* */ }
    recognitionRef.current = null;
  };

  const ensureMicStream = async () => {
    if (streamRef.current && streamRef.current.getTracks().some((t) => t.readyState === "live")) {
      return streamRef.current;
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    return stream;
  };

  const startRecording = async () => {
    try {
      const stream = await ensureMicStream();
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await transcribeAndAppend(blob);
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
      setRecElapsed(0);
      setLiveTranscript("");
      startLivePreview();
      timerRef.current = setInterval(() => setRecElapsed((s) => s + 1), 1000);
    } catch {
      toast.error("Couldn't access the microphone. Check your browser's permissions.", { id: "fam-mic-denied" });
    }
  };
  const stopRecording = () => {
    if (mediaRef.current && recording) {
      mediaRef.current.stop();
      mediaRef.current = null;
      setRecording(false);
      stopLivePreview();
      setLiveTranscript("");
      clearInterval(timerRef.current);
    }
  };
  useEffect(() => {
    return () => {
      try { mediaRef.current?.stop(); } catch { /* */ }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      clearInterval(timerRef.current);
      try { recognitionRef.current?.stop(); } catch { /* */ }
    };
  }, []);

  const transcribeAndAppend = async (blob) => {
    setTranscribing(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "rec.webm");
      fd.append("silo_id", siloId);
      const r = await api.post("/staging/voice-transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const t = (r.data?.text || "").trim();
      if (t) setText((cur) => (cur ? cur.trim() + "\n\n" + t : t));
      else toast("Got an empty transcript. Try again with a louder recording.", { id: "fam-empty-tx" });
    } catch (e) {
      if (e.response?.status === 402 && e.response?.data?.guard_code === "LLM_GUARD") return;
      toast.error(e.response?.data?.detail || "Transcription failed.", { id: "fam-tx-fail" });
    } finally { setTranscribing(false); }
  };

  // ---- Smart create → calendar (approval rules applied server-side) ----
  // Feb 22, 2026 (Randy pt89c-v) — ALL smart-capture calls now go through
  // the async /smart-capture/bulk endpoint, regardless of size. Killing
  // the old sync fork closes the Cloudflare 30s hole that killed 4-person
  // plan parses (typically 3-6K chars — right in the dead zone where the
  // LLM took >30s but my code still routed to sync). Small inputs just
  // become "1 chunk" and complete in one LLM call, same as sync did, but
  // wrapped in the async polling shell that Cloudflare can never kill.
  const [smartBusy, setSmartBusy] = useState(false);
  const [smartItems, setSmartItems] = useState(null);
  const [smartProgress, setSmartProgress] = useState(null);
  const [pingEditor, setPingEditor] = useState(null);
  // Feb 24, 2026 (Randy pt89c-xxii Phase 1) — Smart Edit auto-routing.
  // The Capture Pad has ONE button ("Smart create"). If the text carries
  // an edit-shaped verb, we route to /smart-edit and open the preview
  // modal. Backend re-validates and gracefully falls back to CREATE if
  // no matching events are found. See SmartEditPreviewModal.jsx.
  const [smartEditJobId, setSmartEditJobId] = useState(null);
  // Feb 26, 2026 (Randy pt-smart-create-hijack-fix). See StagingTab.jsx
  // for the full rationale. TL;DR: bare-word matches ("push", "split")
  // hijacked workout/schedule data into /smart-edit. Now we only route
  // to edit when the pad text starts with an imperative edit verb AND
  // is short (≤ 240 chars).
  const EDIT_CMD_RE = /^\s*(?:please\s+)?(?:can\s+you\s+)?(?:change|move|swap|delete|remove|reschedule|replace|reassign|combine|merge|split|shift|postpone|push\s+back|bump|cancel|mark(?:ed)?)\b/i;
  const isEditIntent = (t) => t.length <= 240 && EDIT_CMD_RE.test(t);

  const smartCreate = async () => {
    const t = (text || "").trim();
    if (!t) { toast.error("Nothing to parse yet — write or dictate something first.", { id: "fam-empty-smart" }); return; }
    // Route to Smart Edit if the text reads as an edit request.
    if (isEditIntent(t)) {
      setSmartBusy(true);
      try {
        const r = await api.post(`/calendar/${siloId}/smart-edit`, {
          text: t, tz_offset_minutes: new Date().getTimezoneOffset(),
        });
        const jobId = r.data?.job_id;
        if (!jobId) { toast.error("Couldn't start Smart Edit — try again."); return; }
        setSmartEditJobId(jobId);
      } catch (e) {
        if (e.response?.status === 402 && e.response?.data?.guard_code === "LLM_GUARD") return;
        toast.error(e.response?.data?.detail || "Smart Edit failed.", { id: "fam-smart-edit-fail" });
      } finally { setSmartBusy(false); }
      return;
    }
    setSmartBusy(true); setSmartProgress(null);
    try {
      const r = await api.post(`/calendar/${siloId}/smart-capture/bulk`, {
        text: t, tz_offset_minutes: new Date().getTimezoneOffset(),
      });
      const jobId = r.data?.job_id;
      const totalChunks = r.data?.chunks || 1;
      if (!jobId) { toast.error("Smart create didn't start — try again."); return; }
      // Only show the "N chunks" toast when there's actually more than one;
      // for a single-chunk parse the spinner alone is enough.
      if (totalChunks > 1) {
        toast.success(`Parsing in ${totalChunks} chunks — this may take a minute.`, { id: "fam-bulk-start" });
      }
      setSmartProgress({ done: 0, total: totalChunks });
      let items = []; let done = false; let errMsg = null; let chunkStats = [];
      for (let i = 0; i < 90; i += 1) {
        await new Promise((res) => setTimeout(res, 2000));
        try {
          const pr = await api.get(`/calendar/smart-capture/bulk/job/${jobId}`);
          const st = pr.data?.status;
          const prog = pr.data?.progress;
          if (prog) setSmartProgress(prog);
          if (st === "done") { items = pr.data?.items || []; chunkStats = pr.data?.chunk_stats || []; done = true; break; }
          if (st === "error") { errMsg = pr.data?.error || "Smart create failed."; done = true; break; }
        } catch { /* transient */ }
      }
      if (!done) { toast.error("Smart create is taking longer than expected — try again.", { id: "fam-bulk-slow" }); return; }
      if (errMsg) { toast.error(errMsg, { id: "fam-bulk-err" }); return; }
      const failed = chunkStats.filter((s) => s?.status === "failed");
      if (failed.length) {
        toast.warning(`${failed.length} of ${chunkStats.length} chunk${failed.length === 1 ? "" : "s"} couldn't parse — the rest committed. You can rerun to try again.`, { id: "fam-bulk-partial", duration: 8000 });
      }
      setSmartItems(items);
    } catch (e) {
      if (e.response?.status === 402 && e.response?.data?.guard_code === "LLM_GUARD") return;
      toast.error(e.response?.data?.detail || "Smart create failed.", { id: "fam-smart-fail" });
    } finally { setSmartBusy(false); setSmartProgress(null); }
  };

  const pushToSilo = () => {
    const content = (text || "").trim();
    if (!content) { toast.error("Nothing to push yet — write or dictate something first.", { id: "fam-empty-push" }); return; }
    const title = content.split("\n")[0].slice(0, 80) || "Family note";
    setCommitState({ open: true, text: content, defaultTitle: title });
  };

  const canSmart = owns("ai_calendar") || inheritedSkus.includes("ai_calendar");

  return (
    <section className="bg-white border hairline rounded-lg p-5" data-testid="family-capture-pad">
      <div className="flex items-center gap-2 mb-3">
        <Lightning size={16} weight="duotone" className="text-[#C16E5A]" />
        <h3 className="font-serif-display text-lg text-[#1C1B1A]">Capture pad</h3>
        <InfoTip icon="eye" iconSize={13} placement="bottom-right" width={340} mobileCenter
          testid="family-capture-pad-eye" label="What else can I do with the Capture pad?">
          This isn&apos;t just an amazing way to dictate an event into your family calendar. Its also your way to capture ANY impromptu story, or note and send it directly into your Silo. You keep meaning to remember the clothing sizes for your partner. Want to tell a story about how well your camping trip was, send it to the Silo, you care about, that can relay it to those you want to see it.
        </InfoTip>
        <span className="flex-1" />
        <button
          onClick={() => setText("")}
          disabled={!text}
          data-testid="family-capture-clear"
          title="Clear the capture pad"
          className="text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded border hairline text-[#6B6A68] hover:text-[#C16E5A] hover:border-[#C16E5A] disabled:opacity-40 disabled:hover:text-[#6B6A68] disabled:hover:border-black/10">
          Clear
        </button>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Type or tap Record below. Say things like “dentist Tuesday at 3” and use Smart create."
        rows={6}
        data-testid="family-capture-textarea"
        className="w-full px-3 py-2 border hairline rounded-md text-sm bg-[#FDFCF8] focus:outline-none focus:border-[#4A5D4E] resize-y"
      />
      {recording && (
        <div className="mt-2 px-3 py-2 rounded-md bg-[#FBF1EE] border border-[#E8D5CF] text-xs text-[#6B6A68] italic flex items-start gap-2" data-testid="family-live-transcript">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse mt-1 shrink-0" />
          <span>{liveTranscript || "Listening… speak now."}</span>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 mt-3">
        {!recording ? (
          <button onClick={startRecording} disabled={transcribing} data-testid="family-rec-start"
            className="text-xs px-3 py-2 rounded-md bg-[#C16E5A] text-white hover:bg-[#A55A48] flex items-center gap-1.5 disabled:opacity-50">
            <Microphone size={14} weight="duotone" /> Record
          </button>
        ) : (
          <button onClick={stopRecording} data-testid="family-rec-stop"
            className="text-xs px-3 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5">
            <Stop size={14} weight="fill" /> Stop · {String(Math.floor(recElapsed / 60)).padStart(2, "0")}:{String(recElapsed % 60).padStart(2, "0")}
          </button>
        )}
        {transcribing && <span className="text-xs text-[#6B6A68] flex items-center gap-1"><Spinner size={12} className="animate-spin" /> Transcribing…</span>}
        <span className="flex-1" />
        {canSmart && (
          <button onClick={smartCreate} disabled={!text.trim() || smartBusy} data-testid="family-smart-create"
            title="Let the AI detect events, tasks, routines and pings in this text"
            className="text-xs px-3 py-2 rounded-md border border-[#C19A4A] text-[#8A6A24] bg-[#F8F1E2] hover:bg-[#F3E7CB] flex items-center gap-1.5 disabled:opacity-50">
            {smartBusy ? <Spinner size={14} className="animate-spin" /> : <Sparkle size={14} weight="duotone" />} Smart create
            {smartProgress && smartProgress.total > 0 && (
              <span className="text-[10px] font-medium text-[#8A6A24]/80" data-testid="family-smart-progress">
                {smartProgress.done}/{smartProgress.total}
              </span>
            )}
          </button>
        )}
        <SmartEditUndoCenter siloId={siloId} />
        <button onClick={pushToSilo} disabled={!text.trim()} data-testid="family-push-btn"
          className="text-xs px-3 py-2 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5 disabled:opacity-50">
          <PaperPlaneRight size={14} weight="duotone" /> Push to silo
        </button>
      </div>

      {smartEditJobId && (
        <SmartEditPreviewModal
          siloId={siloId}
          jobId={smartEditJobId}
          onClose={() => setSmartEditJobId(null)}
          onDone={() => { window.dispatchEvent(new Event("mysilo:calendar-refresh")); }}
        />
      )}
      {smartItems && (
        <SmartCaptureModal
          siloId={siloId}
          items={smartItems}
          sourceText={text}
          onAskQA={(q) => { window.location.href = `/s/${siloSlug}?auto_q=${encodeURIComponent((q || "").trim().slice(0, 600))}`; }}
          onSaveAsPing={(q) => { setSmartItems(null); setPingEditor({ kind: "ping", question: (q || "").trim().slice(0, 600), time: "09:00" }); }}
          onClose={() => setSmartItems(null)}
          onCommitted={() => { setSmartItems(null); window.dispatchEvent(new Event("mysilo:calendar-refresh")); }}
        />
      )}
      {pingEditor && (
        <EventEditorModal siloId={siloId} defaults={pingEditor}
          onClose={() => setPingEditor(null)}
          onSaved={() => { setPingEditor(null); window.dispatchEvent(new Event("mysilo:calendar-refresh")); toast.success("Ping saved."); }} />
      )}

      <DocSaveDialog
        open={commitState.open}
        onClose={() => setCommitState({ open: false, text: "", defaultTitle: "" })}
        content={commitState.text}
        sourceSiloId={siloId}
        sourceDoc={null}
        silos={[{ id: siloId, name: siloName || "this silo" }]}
        defaultTitle={commitState.defaultTitle}
        initialTier={defaultPushTier}
        dialogTitle="Push to silo"
        checkOversize={(content, tier) => needsSplit(content, tier)}
        onOversize={({ title, tier }) => {
          setSplitState({ open: true, content: commitState.text, tier, title: title || "" });
          setCommitState({ open: false, text: "", defaultTitle: "" });
        }}
        onSaved={() => setText("")}
      />
      <SplitAndPushModal
        open={splitState.open}
        content={splitState.content}
        defaultTitle={splitState.title}
        siloId={siloId}
        siloName={siloName}
        initialTier={splitState.tier}
        onClose={() => setSplitState((s) => ({ ...s, open: false }))}
        onComplete={() => { setText(""); setSplitState((s) => ({ ...s, open: false })); }}
      />
    </section>
  );
}
